import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { BarChart3, Music2 } from "lucide-react";
import api from "@/lib/api";
import { PracticeGoal } from "@/components/PracticeGoal";

const label = (iso) => new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, { day: "numeric", month: "short" });

const WeekBars = ({ weeks, goal }) => {
  const max = Math.max(1, goal || 0, ...weeks.map((w) => w.minutes));
  return (
    <div data-testid="practice-week-bars">
      <div className="relative flex items-end gap-2 h-28 pt-4">
        {goal > 0 && (
          <div className="absolute left-0 right-0 border-t border-dashed border-[#ffab00]/70 pointer-events-none" style={{ bottom: `${(goal / max) * 100 * 0.86}%` }} title={`Goal: ${goal} min`} data-testid="practice-goal-line" />
        )}
        {weeks.map((w, i) => (
          <div key={w.week} className="relative flex-1 h-full flex items-end">
            <div
              className={`w-full rounded-t-md ${goal && w.minutes >= goal ? "bg-[#ffab00]" : i === weeks.length - 1 ? "bg-[#00f0ff]" : "bg-[#00f0ff]/35"}`}
              style={{ height: `${Math.max(2, (w.minutes / max) * 86)}%` }}
              title={`${w.minutes} min`}
            >
            </div>
          </div>
        ))}
      </div>
      <div className="flex gap-2 mt-1">
        {weeks.map((w, i) => (
          <span key={w.week} className="flex-1 text-center text-[9px] font-tab text-slate-500 whitespace-nowrap leading-tight">
            {i === weeks.length - 1 ? "Now" : label(w.week)}
            <span className="block text-[10px] text-slate-300">{w.minutes ? `${Math.round(w.minutes)}m` : "–"}</span>
          </span>
        ))}
      </div>
    </div>
  );
};

export const PracticeStats = ({ open, onOpenChange }) => {
  const [data, setData] = useState(null);
  const load = () => api.get("/practice/stats").then((r) => setData(r.data)).catch(() => setData(null));
  useEffect(() => {
    if (!open) return;
    load();
  }, [open]);

  const thisWeek = data?.weeks?.[data.weeks.length - 1]?.minutes || 0;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0b0c10] border-white/10 text-slate-100 max-w-xl w-[calc(100%-1.5rem)] rounded-xl" data-testid="practice-stats-modal">
        <DialogHeader>
          <DialogTitle className="font-heading uppercase tracking-tight flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-[#00f0ff]" /> My practice
          </DialogTitle>
          <DialogDescription className="text-slate-400">Time spent playing along with tabs, recorded while the player is running.</DialogDescription>
        </DialogHeader>
        {!data ? (
          <p className="text-sm text-slate-500 py-8 text-center">Loading…</p>
        ) : (
          <div className="flex flex-col gap-6">
            <div className="grid grid-cols-3 gap-3">
              {[["This week", `${Math.round(thisWeek)} min`, "stat-this-week"], ["All time", `${Math.round(data.total_minutes)} min`, "stat-total"], ["Songs", data.songs_count, "stat-songs"]].map(([k, v, id]) => (
                <div key={k} className="rounded-xl border border-white/10 bg-white/[0.03] p-3">
                  <div className="text-[10px] font-tab uppercase tracking-widest text-slate-500">{k}</div>
                  <div className="font-heading text-2xl" data-testid={id}>{v}</div>
                </div>
              ))}
            </div>
            <PracticeGoal data={data} onSaved={load} />
            <div>
              <h4 className="font-heading text-sm uppercase tracking-widest text-[#00f0ff] mb-3">Minutes per week</h4>
              <WeekBars weeks={data.weeks} goal={data.goal} />
            </div>
            <div>
              <h4 className="font-heading text-sm uppercase tracking-widest text-[#ffab00] mb-2">Most played songs</h4>
              {data.top_songs.length === 0 ? (
                <p className="text-sm text-slate-500">Press play on any tab and your practice time will show up here.</p>
              ) : (
                <div className="space-y-1.5" data-testid="practice-top-songs">
                  {data.top_songs.map((s, i) => (
                    <div key={`${s.title}-${s.artist}`} className="flex items-center gap-3 rounded-lg bg-white/[0.03] px-3 py-2">
                      <span className="font-tab text-xs text-slate-500 w-5">#{i + 1}</span>
                      <Music2 className="w-3.5 h-3.5 text-slate-500" />
                      <span className="text-sm truncate flex-1">{s.title}{s.artist ? <span className="text-slate-500"> — {s.artist}</span> : null}</span>
                      <span className="font-tab text-xs text-[#00f0ff] shrink-0">{s.minutes < 1 ? "<1" : Math.round(s.minutes)} min</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
